import React, { useMemo, useState } from 'react';
import { Ruler, ChevronRight, Sparkles } from 'lucide-react';

const CATEGORIES = [
  {
    id: 'tees',
    label: 'Tees & Tops',
    blurb:
      'Our tees feature a signature dropped shoulder and relaxed body. We recommend taking your usual size for the intended oversized look.',
    columns: ['Chest (in)', 'Length (in)', 'Sleeve (in)'],
    rows: [
      { size: 'S', values: ['42 - 44', '28.5', '9.5'], min: 42, max: 44 },
      { size: 'M', values: ['44 - 46', '29.5', '10'], min: 44, max: 46 },
      { size: 'L', values: ['46 - 48', '30.5', '10.5'], min: 46, max: 48 },
      { size: 'XL', values: ['48 - 50', '31.5', '11'], min: 48, max: 50 },
      { size: 'XXL', values: ['50 - 52', '32.5', '11.5'], min: 50, max: 52 },
    ],
  },
  {
    id: 'hoodies',
    label: 'Hoodies & Outerwear',
    blurb:
      'Heavyweight fleece cut for layering. Boxy, oversized fit with a generous chest. Size down if you prefer a cleaner silhouette.',
    columns: ['Chest (in)', 'Length (in)', 'Sleeve (in)'],
    rows: [
      { size: 'S', values: ['46 - 48', '27', '24'], min: 46, max: 48 },
      { size: 'M', values: ['48 - 50', '28', '25'], min: 48, max: 50 },
      { size: 'L', values: ['50 - 52', '29', '26'], min: 50, max: 52 },
      { size: 'XL', values: ['52 - 54', '30', '27'], min: 52, max: 54 },
      { size: 'XXL', values: ['54 - 56', '31', '28'], min: 54, max: 56 },
    ],
  },
  {
    id: 'pants',
    label: 'Pants & Bottoms',
    blurb:
      'Relaxed straight and baggy fits with room through the thigh. Measurements are for the garment laid flat.',
    columns: ['Waist (in)', 'Inseam (in)', 'Hip (in)'],
    rows: [
      { size: 'S', values: ['28 - 30', '30', '40'], min: 28, max: 30 },
      { size: 'M', values: ['30 - 32', '30.5', '42'], min: 30, max: 32 },
      { size: 'L', values: ['32 - 34', '31', '44'], min: 32, max: 34 },
      { size: 'XL', values: ['34 - 36', '31.5', '46'], min: 34, max: 36 },
      { size: 'XXL', values: ['36 - 38', '32', '48'], min: 36, max: 38 },
    ],
  },
];

const FIT_OPTIONS = [
  { value: 'slim', label: 'Slim / True to Size' },
  { value: 'standard', label: 'Standard Streetwear (Oversized)' },
  { value: 'baggy', label: 'Extra Baggy' },
];

// Per-realm accent styling. Falls back to OWLFIT yellow on the standalone page.
const DEFAULT_STYLE = {
  icon: 'text-yellow-400',
  btn: 'bg-yellow-400 text-black',
  rowBg: 'bg-yellow-300',
  rowText: 'text-black',
  youBadge: 'bg-black text-yellow-400',
};
const THEME_STYLES = {
  anime: { icon: 'text-pink-500', btn: 'bg-pink-500 text-white', rowBg: 'bg-pink-500', rowText: 'text-white', youBadge: 'bg-black text-pink-500' },
  tantra: { icon: 'text-red-600', btn: 'bg-red-700 text-white', rowBg: 'bg-red-700', rowText: 'text-white', youBadge: 'bg-black text-red-500' },
  inkspeak: { icon: 'text-yellow-400', btn: 'bg-black text-white', rowBg: 'bg-black', rowText: 'text-yellow-400', youBadge: 'bg-yellow-400 text-black' },
  soul: { icon: 'text-orange-400', btn: 'bg-orange-400 text-black', rowBg: 'bg-orange-300', rowText: 'text-black', youBadge: 'bg-black text-orange-300' },
};

const inputClass =
  'w-full bg-white border-2 border-black px-3 py-2.5 text-sm font-mono text-black placeholder:text-neutral-400 focus:outline-none focus:bg-yellow-50 transition-colors';

const Field = ({ label, children }) => (
  <div>
    <label className="block text-[11px] font-black uppercase tracking-widest text-black mb-2">
      {label}
    </label>
    {children}
  </div>
);

const BodyDiagram = () => (
  <svg width="90" height="130" viewBox="0 0 90 130" fill="none" className="text-black">
    <circle cx="45" cy="16" r="10" stroke="currentColor" strokeWidth="2" />
    <path
      d="M30 30 L60 30 L72 46 L64 54 L60 50 L60 86 L30 86 L30 50 L26 54 L18 46 Z"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinejoin="round"
    />
    <path d="M37 86 L37 122 M53 86 L53 122" stroke="currentColor" strokeWidth="2" />
    <line x1="30" y1="40" x2="60" y2="40" stroke="currentColor" strokeWidth="1.5" strokeDasharray="3 3" />
    <line x1="45" y1="30" x2="45" y2="86" stroke="currentColor" strokeWidth="1.5" strokeDasharray="3 3" opacity="0.4" />
  </svg>
);

const SizeGuidePanel = ({ defaultCategory = 'tees', theme }) => {
  const [activeId, setActiveId] = useState(defaultCategory);
  const [form, setForm] = useState({ height: '', weight: '', chest: '', fit: 'standard' });
  const [result, setResult] = useState(null);

  const active = useMemo(
    () => CATEGORIES.find((c) => c.id === activeId) || CATEGORIES[0],
    [activeId]
  );

  const styleSet = THEME_STYLES[theme?.id] || DEFAULT_STYLE;

  const setField = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));

  const calculateFit = () => {
    const chest = parseFloat(form.chest);
    if (!chest) {
      setResult({ error: 'Enter your chest measurement to get a recommendation.' });
      return;
    }
    const rows = active.rows;
    let best = rows[0];
    let bestDiff = Infinity;
    rows.forEach((row) => {
      const mid = (row.min + row.max) / 2;
      const diff = Math.abs(chest - mid);
      if (diff < bestDiff) {
        bestDiff = diff;
        best = row;
      }
    });
    let index = rows.indexOf(best);
    if (form.fit === 'baggy' && index < rows.length - 1) index += 1;
    if (form.fit === 'slim' && index > 0) index -= 1;
    setResult({ size: rows[index].size });
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-[230px_1fr] gap-6 text-black">
      {/* Sidebar */}
      <aside className="space-y-6">
        <nav className="space-y-2">
          {CATEGORIES.map((c) => {
            const isActive = c.id === activeId;
            return (
              <button
                key={c.id}
                onClick={() => {
                  setActiveId(c.id);
                  setResult(null);
                }}
                className={`w-full flex items-center justify-between px-3 py-2.5 border-2 border-black text-sm font-black uppercase tracking-tight transition-all ${
                  isActive
                    ? 'bg-black text-white shadow-[4px_4px_0px_black]'
                    : 'bg-white text-black hover:bg-yellow-300'
                }`}
              >
                <span>{c.label}</span>
                {isActive && <ChevronRight size={16} />}
              </button>
            );
          })}
        </nav>

        <div className="bg-white border-4 border-black shadow-[6px_6px_0px_black]">
          <div className="bg-black px-4 py-3">
            <h3 className={`font-black uppercase text-sm tracking-widest ${styleSet.icon}`}>
              How to Measure
            </h3>
          </div>
          <div className="px-4 py-6 flex items-center justify-center border-b-2 border-black bg-neutral-100">
            <BodyDiagram />
          </div>
          <ul className="p-4 space-y-2 text-xs font-mono text-neutral-700">
            <li className="flex gap-2">
              <span className="font-black text-black">CHEST:</span> Under arms
            </li>
            <li className="flex gap-2">
              <span className="font-black text-black">LENGTH:</span> Shoulder to hem
            </li>
            <li className="flex gap-2">
              <span className="font-black text-black">SLEEVE:</span> Shoulder to cuff
            </li>
          </ul>
        </div>
      </aside>

      {/* Main */}
      <main className="space-y-8">
        {/* Recommender */}
        <section className="bg-white border-4 border-black shadow-[8px_8px_0px_black]">
          <div className="bg-black px-5 py-4 flex items-center gap-3">
            <Ruler size={22} className={styleSet.icon} />
            <h2 className="text-xl md:text-2xl font-black uppercase tracking-tighter text-white">
              Size Recommender
            </h2>
          </div>
          <div className="p-5 md:p-6">
            <p className="text-neutral-600 text-sm font-mono mb-6 max-w-md">
              Enter your exact measurements to get a personalized fit recommendation for OWLFIT
              streetwear cuts.
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field label="Height (cm)">
                <input
                  type="number"
                  value={form.height}
                  onChange={(e) => setField('height', e.target.value)}
                  placeholder="175"
                  className={inputClass}
                />
              </Field>
              <Field label="Weight (kg)">
                <input
                  type="number"
                  value={form.weight}
                  onChange={(e) => setField('weight', e.target.value)}
                  placeholder="70"
                  className={inputClass}
                />
              </Field>
              <Field label="Chest (in)">
                <input
                  type="number"
                  value={form.chest}
                  onChange={(e) => setField('chest', e.target.value)}
                  placeholder="38"
                  className={inputClass}
                />
              </Field>
              <Field label="Preferred Fit">
                <select
                  value={form.fit}
                  onChange={(e) => setField('fit', e.target.value)}
                  className={`${inputClass} cursor-pointer`}
                >
                  {FIT_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
              </Field>
            </div>

            <div className="mt-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              {result?.size ? (
                <div className={`flex items-center gap-3 border-2 border-black px-4 py-2 ${styleSet.rowBg} ${styleSet.rowText}`}>
                  <Sparkles size={18} />
                  <span className="text-sm font-bold">
                    We recommend{' '}
                    <strong className="text-base font-black">Size {result.size}</strong>
                  </span>
                </div>
              ) : result?.error ? (
                <span className="text-sm font-bold text-red-600">{result.error}</span>
              ) : (
                <span />
              )}
              <button
                onClick={calculateFit}
                className={`${styleSet.btn} border-2 border-black px-5 py-2.5 font-black uppercase text-xs tracking-widest shadow-[4px_4px_0px_black] hover:translate-x-1 hover:translate-y-1 hover:shadow-none transition-all whitespace-nowrap`}
              >
                Calculate My Fit
              </button>
            </div>
          </div>
        </section>

        {/* Chart */}
        <section>
          <h2 className="text-2xl md:text-3xl font-black uppercase tracking-tighter">{active.label}</h2>
          <p className="text-neutral-600 text-sm font-mono mt-2 max-w-xl">{active.blurb}</p>

          <div className="mt-5 border-4 border-black shadow-[8px_8px_0px_black] overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-black text-white uppercase text-xs tracking-widest">
                  <th className="text-left font-black px-4 py-3">Size</th>
                  {active.columns.map((col) => (
                    <th key={col} className="text-left font-black px-4 py-3">
                      {col}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {active.rows.map((row, i) => {
                  const highlighted = result?.size === row.size;
                  return (
                    <tr
                      key={row.size}
                      className={`border-t-2 border-black transition-colors ${
                        highlighted
                          ? `${styleSet.rowBg} ${styleSet.rowText}`
                          : i % 2 === 1
                          ? 'bg-neutral-100'
                          : 'bg-white'
                      }`}
                    >
                      <td className="px-4 py-3 font-black uppercase">
                        {row.size}
                        {highlighted && (
                          <span className={`ml-2 text-[10px] font-black uppercase px-1.5 py-0.5 ${styleSet.youBadge}`}>
                            You
                          </span>
                        )}
                      </td>
                      {row.values.map((v, idx) => (
                        <td
                          key={idx}
                          className={`px-4 py-3 font-mono ${highlighted ? '' : 'text-neutral-800'}`}
                        >
                          {v}
                        </td>
                      ))}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <p className="text-xs text-neutral-500 font-mono mt-4">
            // Measurements are taken with garment laying flat.
          </p>
        </section>
      </main>
    </div>
  );
};

export default SizeGuidePanel;
